/**
 * The product pages that used to be the landing page.
 *
 * ========================== WHERE THIS CAME FROM ===============================
 * PHASE 2B moved Home: it is the saloon now — three doors, your name, your Bucks and
 * the chalkboard — because the master plan §4 says the front door is a room and not a
 * brochure. This is everything the brochure said, moved wholesale and not rewritten,
 * and it is rendered on `/about`.
 *
 * NOTHING WAS DROPPED. Four sections came across: the two product summaries (Trainer
 * and Play), "What we will not tell you", "The deck is checkable" and "What it costs".
 * The hero — the display line and the sign-up buttons — is the one thing that did not,
 * because the saloon itself is now what a visitor lands on and it has its own way in.
 *
 * The claims discipline that shaped the original is the reason it is moved intact:
 * every limit is stated on the page rather than buried, and a paraphrase of a claim
 * about accuracy is a new claim about accuracy.
 */
import { Link } from "react-router-dom";
import { useMeContext } from "../lib/MeContext";
import { money } from "../lib/api";
import { PRICING, UNLOCKS } from "@config/plans";

export function LandingSections() {
  const { me } = useMeContext();
  /*
    Signed out there is no /api/me, so the price falls back to the SAME constant the
    Worker and Stripe read. It used to be the literal 1899 — a second copy of the
    price, shown only to people who have not bought yet, which is the worst place for
    it to quietly disagree with the real one.
  */
  const monthly = me?.pricing.monthly.amountCents ?? PRICING.monthly.amountCents;

  return (
    <div className="page home">
      {/* -------------------------------------------------------- products --- */}
      <section className="two-up">
        <div>
          <h2>Trainer</h2>
          <p>
            One spot at a time, heads-up through nine-handed, ten to twenty-five big
            blinds, across every ICM context from early to the final table. Shove or
            fold, and see what the decision cost in chips and in equity.
          </p>
          <p className="muted small">
            Every chart came out of our own solver, and every chart shows you the
            precision it was measured at — the real gap between our model and the game,
            on the chart itself. Where a combination cannot be solved inside that
            precision it is withheld, and marked as withheld.
          </p>
        </div>
        <div>
          <h2>Play</h2>
          <p>
            A real table of bots that decide for themselves — from players who cannot
            fold anything to ones who punish every loose call. Two to nine seats, cash
            or tournament, any stack depth, every hand feeding your leak report.
          </p>
          <p className="muted small">
            It runs entirely in your browser, which is why it is free and why it works
            on a plane. After each hand you can see what every bot was thinking.
          </p>
        </div>
      </section>

      {/* --------------------------------------------------------- honesty --- */}
      <section className="claims">
        <h2>What we will not tell you</h2>
        <p className="muted">
          "GTO" is printed on every poker product sold. It is worth almost nothing
          unless somebody will say where it stops applying, so here is where.
        </p>
        <ul>
          <li>
            <strong>The bots are not a solver.</strong> They play a heuristic model
            with a deliberately varied skill ladder, and the price we put on your
            decision comes from that same model. It is a good sparring partner. It is
            not an equilibrium, and we never call it one.
          </li>
          <li>
            <strong>The solved charts stop where the solve stopped.</strong> Short
            stacks, push/fold, ICM. That is a real product with real edges, and it is
            not "every spot in poker".
          </li>
          <li>
            <strong>Not every chart is equally precise, and each one says so.</strong>{" "}
            Our solver treats an all-in as facing one caller. That is exact heads-up and
            an approximation at a full table, so we measure what it costs by dealing
            hundreds of thousands of real hands and print the answer on the chart.
            Knockout charts sit at the loose end of that range, because a bounty makes
            everybody call wider and wider calls are exactly what the simplification
            cannot see. We would rather show you the number than average it away.
          </li>
          <li>
            <strong>No training software can promise you a result.</strong> Poker is a
            game of skill played for stakes we have nothing to do with, and most people
            who play it for money lose money.
          </li>
        </ul>
      </section>

      {/* ---------------------------------------------------------- shuffle --- */}
      <section className="claims">
        <h2>The deck is checkable</h2>
        <p>
          Every hand is dealt from a 256-bit seed and a stream cipher, and every hand
          records the seed it came from alongside a hash published before the cards came
          out. You can re-deal any hand yourself and confirm the deck was fixed before
          you acted — in your own browser, not on our word for it.
        </p>
        <p>
          <Link to="/fair">How the shuffle works, and what we do not claim about it →</Link>
        </p>
      </section>

      {/* ---------------------------------------------------------- pricing --- */}
      <section className="claims">
        <h2>What it costs</h2>
        <p>
          <strong>Free</strong> is the whole game against every bot, plus ten solved
          spots a day. <strong>Pro</strong> is {money(monthly)} a month: no daily limit,
          every published chart and ICM context, and every decision graded against the
          solve.
        </p>
        {/* PHASE 9, master plan §9: the other rooms' analysis, and that Pro includes it. */}
        <p>
          The tables are free in every room. Blackjack&rsquo;s exact numbers, its Count
          Drill and Decision Spots are <strong>Blackjack Pro</strong>,{" "}
          {money(UNLOCKS.blackjack_pro.amountCents)} once;
          cribbage&rsquo;s will be <strong>Cribbage Pro</strong>,{" "}
          {money(UNLOCKS.cribbage_pro.amountCents)} once, when that room opens. Poker Pro
          includes both.
        </p>
        <p><Link to="/pricing">What Pro includes →</Link></p>
      </section>
    </div>
  );
}
